import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Pause, Play, X } from "lucide-react";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useArchViz } from "./ArchVizContext";

const STEP_INTERVAL_MS = 1800;

export function FlowPlayer() {
  const { state, dispatch } = useArchViz();
  const arch = state.architecture;
  const flow = state.activeFlowId
    ? arch?.dataFlows.find((f) => f.id === state.activeFlowId)
    : undefined;

  useEffect(() => {
    if (!flow || !state.flowPlaying) return;
    const id = window.setInterval(() => {
      const last = flow.steps.length - 1;
      if (state.flowStepIndex >= last) {
        dispatch({ type: "TOGGLE_PLAY" });
      } else {
        dispatch({ type: "STEP_FLOW", delta: 1 });
      }
    }, STEP_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [flow, state.flowPlaying, state.flowStepIndex, dispatch]);

  if (!flow) return null;
  const step = flow.steps[state.flowStepIndex];
  if (!step) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        className="absolute bottom-12 left-1/2 -translate-x-1/2 bg-card border rounded-xl shadow-lg px-4 py-3 flex items-center gap-3 min-w-[480px] max-w-[640px]"
      >
        <div className="flex flex-col min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{flow.label}</span>
            <span>·</span>
            <span>
              Step {state.flowStepIndex + 1} of {flow.steps.length}
            </span>
          </div>
          <div className="text-sm truncate mt-0.5">
            <span className="font-mono text-xs text-muted-foreground">{step.source}</span>
            <ChevronRight className="inline h-3 w-3 mx-1" />
            <span className="font-mono text-xs text-muted-foreground">{step.target}</span>
            <span className="ml-2 text-foreground">{step.description}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => dispatch({ type: "STEP_FLOW", delta: -1 })}
            disabled={state.flowStepIndex === 0}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => dispatch({ type: "TOGGLE_PLAY" })}>
            {state.flowPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => dispatch({ type: "STEP_FLOW", delta: 1 })}
            disabled={state.flowStepIndex >= flow.steps.length - 1}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => dispatch({ type: "STOP_FLOW" })}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
